import styled, { css, keyframes } from "styled-components";

/*
 * Proizvod detalj, v2 redizajn (s.6): breadcrumb, sticky galerija 55%,
 * info kolona 45%, čista cena, jedan tok sadržaja ispod (bez tabova).
 *
 * Animacije: galerija ulazi sleva + info kolona stepenasto na mount,
 * sekcije ispod na scroll (useReveal), crossfade slike pri promeni thumb-a.
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-26px); }
  to { opacity: 1; transform: translateX(0); }
`;

const imageIn = keyframes`
  from { opacity: 0; transform: scale(1.03); }
  to { opacity: 1; transform: scale(1); }
`;

/* Reveal na scroll, transition varijanta za sekcije ispod fold-a */
const revealBase = css`
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? "0" : "26px")});
  transition:
    opacity 0.6s ease,
    transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

export const Page = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 1.75rem 1.5rem 5rem;

  /* prostor za sticky CTA traku na mobilnom */
  @media (max-width: 768px) {
    padding-bottom: 7rem;
  }
`;

/* Sticky "Dodaj u korpu" traka, samo mobilno (v2 s.7).
   Prikazuje se tek kad glavno CTA dugme izađe iz vidika ($visible). */
export const MobileCTABar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1500;
    padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom));
    background: rgba(19, 16, 16, 0.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-top: 1px solid var(--border-strong);
    transform: translateY(${(p) => (p.$visible ? "0" : "110%")});
    transition: transform 0.25s ease;
  }
`;

export const MobileCTAPrice = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  strong {
    color: var(--brand);
    font-size: 1.15rem;
    font-weight: 700;
    white-space: nowrap;
  }

  span {
    color: var(--text-muted);
    font-size: 0.68rem;
  }
`;

export const MobileCTAButton = styled.button`
  flex: 1;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    background: var(--bg-elevated);
    color: var(--text-muted);
  }
`;

/* ------------------------------- breadcrumb ------------------------------ */
export const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.75rem;
  font-size: 0.85rem;
  animation: ${fadeUp} 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  a,
  span.link {
    color: var(--text-secondary);
    text-decoration: none;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: var(--text-primary);
    }
  }

  .sep {
    color: var(--text-muted);
  }

  .current {
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 320px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const MobileBack = styled.button`
  display: none;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  padding: 0 0 1.25rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  cursor: pointer;

  svg {
    color: var(--brand);
  }

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

/* -------------------------------- layout -------------------------------- */
export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 55fr) minmax(0, 45fr);
  gap: 3rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const GalleryCol = styled.div`
  position: sticky;
  top: 84px;
  animation: ${slideInLeft} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) 0.08s backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 900px) {
    position: static;
  }
`;

export const MainImage = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  background: #ffffff;
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
  cursor: zoom-in;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    /* crossfade pri promeni slike (key={selectedImage}) */
    animation: ${imageIn} 0.3s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      animation: none;
    }
  }

  .zoom {
    position: absolute;
    right: 12px;
    bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: var(--radius-control);
    background: rgba(0, 0, 0, 0.55);
    color: #ffffff;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .zoom {
    opacity: 1;
  }
`;

export const ThumbRail = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* thumbovi ulete jedan za drugim posle galerije */
  & > * {
    animation: ${fadeUp} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 8 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${350 + i * 50}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

export const Thumb = styled.button`
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  padding: 0;
  background: #ffffff;
  border: ${(p) =>
    p.$active ? "2px solid var(--brand)" : "1px solid var(--border)"};
  border-radius: var(--radius-control);
  overflow: hidden;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/* ------------------------------ info kolona ------------------------------ */
export const InfoCol = styled.div`
  min-width: 0;

  /* sadržaj ulazi stepenasto: chip → naslov → cena → veličine → ... */
  & > * {
    animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 8 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${150 + i * 75}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

export const BandChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 30px;
  padding: 0 0.85rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  color: var(--brand);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: border-color 0.18s ease;

  &:hover {
    border-color: var(--brand);
  }
`;

export const Title = styled.h1`
  margin: 0.9rem 0 1rem;
  font-family: var(--font-display);
  font-size: clamp(32px, 3.2vw, 40px);
  line-height: 1.02;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

export const PriceBlock = styled.div`
  margin-bottom: 1.75rem;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const Price = styled.span`
  font-size: 30px;
  font-weight: 700;
  color: var(--brand);
`;

export const OldPrice = styled.span`
  font-size: 1rem;
  color: var(--text-muted);
  text-decoration: line-through;
`;

export const DiscountTag = styled.span`
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-pill);
  background: var(--brand-dim);
  color: var(--brand);
  font-size: 0.78rem;
  font-weight: 700;
`;

/* -------------------------------- veličine ------------------------------- */
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
`;

export const Block = styled.div`
  margin-bottom: 1.5rem;

  &.shake {
    animation: ${shake} 0.35s ease;
  }
`;

export const BlockLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.6rem;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
`;

export const SizeGuideLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: none;
  padding: 0;
  color: var(--text-secondary);
  font-size: 0.82rem;
  cursor: pointer;
  transition: color 0.15s ease;

  svg {
    color: var(--brand);
  }

  &:hover {
    color: var(--text-primary);
    text-decoration: underline;
  }
`;

export const SizeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const SizeBtn = styled.button`
  min-width: 52px;
  height: 42px;
  padding: 0 0.8rem;
  border-radius: var(--radius-control);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  background: ${(p) => (p.$selected ? "var(--brand-dim)" : "transparent")};
  color: ${(p) =>
    p.disabled
      ? "var(--text-muted)"
      : p.$selected
        ? "var(--brand)"
        : "var(--text-primary)"};
  border: ${(p) =>
    p.$selected ? "2px solid var(--brand)" : "1px solid var(--border-strong)"};
  text-decoration: ${(p) => (p.disabled ? "line-through" : "none")};

  &:hover:not(:disabled) {
    border-color: var(--brand);
  }

  &:disabled {
    cursor: not-allowed;
    border-color: var(--border);
  }
`;

/* Mera proizvoda bez veličina (bedž, ceger...) — prikaz, ne bira se */
export const MeasurePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 42px;
  padding: 0 0.9rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
`;

export const SizeError = styled.div`
  margin-top: 0.6rem;
  color: var(--danger);
  font-size: 0.85rem;
`;

/* ----------------------------- količina + CTA ---------------------------- */
export const QuantityControl = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  overflow: hidden;
`;

export const QtyBtn = styled.button`
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.06);
  }

  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

export const QtyInput = styled.input`
  width: 52px;
  height: 42px;
  text-align: center;
  background: transparent;
  border: none;
  border-left: 1px solid var(--border);
  border-right: 1px solid var(--border);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  font-family: var(--font-body);

  &:focus {
    outline: none;
  }
`;

export const CTARow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin: 1.5rem 0;
`;

export const AddToCartBtn = styled.button`
  flex: 1;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.18s ease,
    transform 0.18s ease;

  &:hover:not(:disabled) {
    background: var(--brand-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    background: var(--bg-elevated);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

export const ShareBtn = styled.button`
  width: 52px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: var(--brand);
    color: var(--brand);
  }
`;

export const TrustRow = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border);
`;

export const TrustItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--text-secondary);
  font-size: 13px;

  svg {
    color: var(--brand);
    flex-shrink: 0;
  }
`;

/* --------------------------- sadržaj ispod (full) ------------------------- */
export const BelowSection = styled.section`
  margin-top: 4rem;
  max-width: 720px;
  ${revealBase}
`;

export const SectionLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
`;

export const SectionH2 = styled.h2`
  margin: 0 0 1.25rem;
  font-family: var(--font-display);
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

export const Prose = styled.div`
  color: var(--text-secondary);
  font-size: 0.97rem;
  line-height: 1.75;

  p {
    margin: 0 0 1rem;
  }
`;

export const SpecDl = styled.dl`
  margin: 1.75rem 0 0;

  div {
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 1rem;
    padding: 0.65rem 0;
    border-bottom: 1px solid var(--border);

    @media (max-width: 560px) {
      grid-template-columns: 1fr;
      gap: 0.15rem;
    }
  }

  dt {
    color: var(--text-muted);
    font-size: 0.85rem;
  }

  dd {
    margin: 0;
    color: var(--text-primary);
    font-size: 0.92rem;
  }
`;

/* ------------------------------ još od benda ----------------------------- */
export const MoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 24px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

export const MoreSection = styled.section`
  margin-top: 4rem;
  ${revealBase}

  /* kartice u gridu ulete stepenasto kad sekcija postane vidljiva */
  ${(p) =>
    p.$visible &&
    css`
      ${MoreGrid} > * {
        animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      }

      ${Array.from(
        { length: 4 },
        (_, i) => css`
          ${MoreGrid} > *:nth-child(${i + 1}) {
            animation-delay: ${150 + i * 80}ms;
          }
        `,
      )}
    `}
`;

export const BannerLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  margin-top: 1.5rem;
  height: 52px;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  svg {
    transition: transform 0.18s ease;
  }

  &:hover {
    border-color: var(--brand);
    color: var(--brand);

    svg {
      transform: translateX(4px);
    }
  }
`;

/* ------------------------------ modal + misc ----------------------------- */
export const ImageModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  cursor: zoom-out;
`;

export const ModalContent = styled.div`
  position: relative;
  max-width: min(90vw, 820px);
  max-height: 90vh;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: var(--radius-card);
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: -14px;
  right: -14px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-elevated);
  border: 1px solid var(--border-strong);
  border-radius: 50%;
  color: var(--text-primary);
  font-size: 1.2rem;
  cursor: pointer;

  &:hover {
    color: var(--brand);
  }
`;

export const StateContainer = styled.div`
  padding: 5rem 1rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 1rem;
`;
